/* eslint-disable import/no-unresolved */
import { forEach } from 'lodash';
import daviplataByWavaIcon from "images/integrations/daviplata_by_wava.png";
import mercatelyIcon from "images/integrations/mercately.png";
import nequiByWavaIcon from "images/integrations/nequi_by_wava.png";

import WavaModelMC from '../presentation/models/WavaModelMC';
import { uuid } from '../util/utils';
import { getDocumentCode } from '../util/DocumentCode';
import { generateExternalKey } from '../util/externalKeyUtils';

// Iconos para métodos de pago
export const paymentMethodIcons = {
  daviplata: daviplataByWavaIcon,
  nequi: nequiByWavaIcon,
  mercately: mercatelyIcon
};

/**
 * Serializa los métodos de pago desde la respuesta de la API
 * @param {Array} data - Array de gateways de la API
 * @param {Object} retailerInfo - Información del retailer
 * @param {Object} order - Orden opcional para generar link de Mercately
 * @returns {Array} Array de métodos de pago serializados
 */
export const serializePaymentMethods = (data, retailerInfo, order = null) => {
  const result = data.flatMap((item) => {
    const provider = item.payment_gateway_provider || {};

    if (provider.key === 'wava') {
      return [
        {
          active: item.active,
          name: 'Nequi',
          key: 'nequi',
          icon: paymentMethodIcons.nequi,
          selected: true,
          link: 'www.nequi.com.co'
        },
        {
          active: item.active,
          name: 'Daviplata',
          key: 'daviplata',
          icon: paymentMethodIcons.daviplata,
          selected: true,
          link: 'www.daviplata.com.co'
        }
      ];
    }
    return item;
  });

  // Mercately solo se muestra con orden: el link de checkout requiere order (web_id)
  if (order && (retailerInfo.stripe_integrated || retailerInfo['payphone_connected?'] || retailerInfo['mercado_pago_integrated?'])) {
    const shopLink = getShopLink(order);
    result.push({
      name: 'Mercately',
      key: 'mercately',
      icon: paymentMethodIcons.mercately,
      selected: true,
      link: shopLink
    });
  }

  return result;
};

/**
 * Obtiene el link de checkout de Mercately
 * @param {Object} order - Orden opcional
 * @param {boolean} includePrefix - Si incluir el prefijo "Mercately: " (default: false)
 * @returns {string} Link de checkout
 */
export const getShopLink = (order = null, includePrefix = false) => {
  const baseLink = order?.web_id 
    ? `${ENV.SHOPS_BASE_URL}/checkout?order=${order.web_id}`
    : `${ENV.SHOPS_BASE_URL}/checkout`;
  
  return includePrefix ? `Mercately: ${baseLink}` : baseLink;
};

/**
 * Genera el link de pago de Wava (Nequi/Daviplata)
 * @param {Object} order - Orden con información del cliente y monto
 * @returns {Promise<string>} Link de pago de Wava
 */
export const generateLinkForWava = async (order = null, retailerId = '0') => {
  const customer = order?.customer || {};
  
  // Si hay orden, usar sus datos; si no, usar valores por defecto
  const amount = order ? Number(order.total || 0) : 0;
  const description = order 
    ? (order.web_id ? `Quick purchase - ${order.web_id}` : (order.description || 'Quick purchase'))
    : 'Generic payment';
  const orderKey = order?.web_id || uuid();

  const params = {
    link_data: {
      amount,
      description,
      user: {
        first_name: customer?.first_name,
        last_name: customer?.last_name,
        email: customer?.email,
        phone_number: String(customer?.phone || ''),
        country: customer?.country_id,
        id_number: customer?.id_number,
        id_type: getDocumentCode(customer?.country_id, customer?.id_type, { fallback: 'CI' })
      },
      redirect_link: '',
      order_key: generateExternalKey(orderKey, retailerId)
    }
  };

  try {
    const res = await WavaModelMC.linkBy(params);
    return res.link;
  } catch (err) {
    if (typeof window !== 'undefined' && window.showtoast) {
      window.showtoast(err.message || 'Error al generar link de pago');
    }
    throw err;
  }
};

/**
 * Formatea los links de pago para mostrarlos en un mensaje
 * @param {Array} selectedPaymentMethods - Métodos de pago seleccionados
 * @param {string} wavaLink - Link de Wava (opcional, para Nequi/Daviplata)
 * @param {Object} order - Orden opcional para generar link de Mercately
 * @returns {string} Texto formateado con los links
 */
export const formatPaymentLinks = (selectedPaymentMethods, wavaLink = null, order = null) => {
  let newLink = '';
  
  forEach(selectedPaymentMethods, (method) => {
    if (method.key === 'mercately') {
      const shopLink = getShopLink(order, true);
      newLink += `${newLink ? '\n' : ''}${shopLink}`;
    } else if (wavaLink && (method.key === 'nequi' || method.key === 'daviplata')) {
      newLink += `${newLink ? '\n' : ''}${method.name}: ${wavaLink}`;
    }
  });

  return newLink;
};

/**
 * Actualiza los métodos de pago con los links reales generados
 * @param {Array} paymentMethods - Métodos de pago seleccionados
 * @param {string} wavaLink - Link de Wava generado (opcional)
 * @param {Object} order - Orden para generar link de Mercately (opcional)
 * @returns {Array} Métodos de pago actualizados con links reales
 */
export const updatePaymentMethodsWithLinks = (paymentMethods, wavaLink = null, order = null) => {
  return paymentMethods.map((method) => {
    // Actualizar métodos de Wava con el link real
    if (wavaLink && (method.key === 'nequi' || method.key === 'daviplata')) {
      return {
        ...method,
        link: wavaLink
      };
    }
    
    // Actualizar link de Mercately
    if (method.key === 'mercately') {
      return {
        ...method,
        link: getShopLink(order)
      };
    }
    
    return method;
  });
};

/**
 * Verifica si hay métodos de Wava (Nequi o Daviplata) seleccionados
 * @param {Array} selectedPaymentMethods - Métodos de pago seleccionados
 * @returns {boolean} True si hay métodos de Wava seleccionados
 */
export const hasWavaMethods = (selectedPaymentMethods) => {
  return selectedPaymentMethods.some(
    (item) => item.key === 'nequi' || item.key === 'daviplata'
  );
};

/**
 * Toggle de selección de un método de pago
 * @param {Array} paymentMethods - Array de métodos de pago
 * @param {Object} methodToToggle - Método a togglear
 * @returns {Array} Nuevo array con el método toggleado
 */
export const togglePaymentMethodSelection = (paymentMethods, methodToToggle) => {
  return paymentMethods.map((meth) => {
    if (meth.key === methodToToggle.key) {
      return { ...meth, selected: !meth.selected };
    }
    return meth;
  });
};

